import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { pick } from "../i18n/translations";
import { Sparkles, ArrowRight } from "lucide-react";
import DealOfMomentHero from "../components/DealOfMomentHero";
import Seo from "../components/Seo";

export default function CataloguePage() {
  const { lang } = useApp();
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({});
  const [hoveredCat, setHoveredCat] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCats(r.data || []);
      (r.data || []).forEach((c) => {
        api.get(`/products?category=${c.slug}&limit=200`).then((rc) => {
          setCounts((prev) => ({ ...prev, [c.slug]: (rc.data || []).length }));
        });
      });
    });
  }, []);

  const topLevel = cats.filter((c) => !c.parent_slug && c.active !== false);
  const childrenOf = (slug) => cats.filter((c) => c.parent_slug === slug && c.active !== false);

  return (
    <div className="bg-ivory min-h-screen">
      <Seo
        title="Catalogue d'orthophonie — 14 univers thérapeutiques"
        description="Catalogue complet de matériel d'orthophonie : phonologie, langage oral, langage écrit, articulation, voix, calcul, mémoire, attention, autisme, troubles dys. Curaté par des cliniciens."
        path="/catalogue"
        keywords="catalogue orthophonie, jeux orthophonie en ligne, matériel rééducation, phonologie, dyslexie, dyspraxie, troubles dys"
      />
      <DealOfMomentHero />

      {/* CATÉGORIES grille premium */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">
            <Link to="/" className="hover:underline">Accueil</Link> · Catalogue
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight" data-testid="catalogue-title">
            Explorer notre <span className="italic">catalogue</span>
          </h1>
          <p className="mt-4 text-ink-soft text-lg max-w-2xl">
            14 univers professionnels — chacun curaté par des cliniciens experts. Cliquez sur une catégorie pour découvrir nos sélections.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalogue-grid">
          {topLevel.map((c, idx) => {
            const subs = childrenOf(c.slug);
            const count = counts[c.slug] ?? "...";
            return (
              <div
                key={c.slug}
                onMouseEnter={() => setHoveredCat(c.slug)}
                onMouseLeave={() => setHoveredCat(null)}
                className="relative"
              >
                <Link
                  to={`/categorie/${c.slug}`}
                  data-testid={`cat-card-${c.slug}`}
                  className="group block relative overflow-hidden rounded-3xl bg-white border border-ink/10 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] transition-all duration-500 hover:-translate-y-1"
                >
                  {/* Image background */}
                  <div className="relative aspect-[5/3] overflow-hidden bg-ivory-200">
                    {c.image ? (
                      <img
                        src={c.image}
                        alt={pick(c.name, lang)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pedablue/20 to-sage/30" />
                    )}
                    {/* Subtle gradient overlay for legibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/10 to-transparent" />
                    {/* Badge produit count */}
                    <span className="absolute top-4 end-4 bg-ivory/95 text-ink text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
                      {count} {typeof count === "number" && count === 1 ? "produit" : "produits"}
                    </span>
                    {/* Index sticker */}
                    <span className="absolute top-4 start-4 font-display text-3xl text-ivory/80 italic">{String(idx + 1).padStart(2, "0")}</span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="font-display text-2xl leading-tight group-hover:text-pedablue transition-colors mb-2">
                      {pick(c.name, lang)}
                    </h2>
                    <p className="text-sm text-ink-soft line-clamp-2 mb-4">{pick(c.description, lang) || "Sélection clinicienne professionnelle"}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest text-pedablue inline-flex items-center gap-1">
                        Explorer
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                      {subs.length > 0 && (
                        <span className="text-[10px] text-ink-soft italic">{subs.length} sous-catégorie{subs.length > 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </div>
                </Link>

                {/* Hover: subcategories floating panel */}
                {hoveredCat === c.slug && subs.length > 0 && (
                  <div
                    className="absolute z-30 top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-ink/10 p-4 animate-in fade-in slide-in-from-top-2 duration-200"
                    data-testid={`cat-subs-${c.slug}`}
                  >
                    <p className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Sous-catégories</p>
                    <div className="space-y-1.5">
                      {subs.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/categorie/${s.slug}`}
                          className="block text-sm hover:text-pedablue transition-colors py-1.5 border-b border-ink/5 last:border-0"
                        >
                          ↳ {pick(s.name, lang)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA bottom */}
        <div className="mt-20 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-ink-soft mb-3">Tu ne trouves pas ?</p>
          <h3 className="font-display text-3xl mb-4 italic">{"Demande-nous"}</h3>
          <p className="text-ink-soft mb-6 max-w-md mx-auto">
            On sourcera spécifiquement pour toi le matériel adapté à ta pratique.
          </p>
          <a href="mailto:contact@langageonline.fr" className="btn-primary inline-flex">
            <Sparkles size={14} className="me-2" />
            Nous écrire
          </a>
        </div>
      </section>
    </div>
  );
}
